// @ts-check

import Layer from 'ol/layer/Layer';
import VectorSource from 'ol/source/Vector';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { OSM, XYZ, ImageStatic, TileWMS} from 'ol/source';
import View from 'ol/View';
import { getCenter, getWidth } from 'ol/extent';
import Map from 'ol/Map';
import Polygon from 'ol/geom/Polygon';
import Feature from 'ol/Feature';
import { Stroke, Fill, Style } from 'ol/style';
import { get as getProjection } from 'ol/proj';
import ImageLayer from 'ol/layer/Image'
import { transformExtent } from "ol/proj.js"
import {register} from 'ol/proj/proj4'
import proj4 from 'proj4'
import Select from 'ol/interaction/Select';

class MainMap {

    /**
     * @param {String} id id of html element
     */

    constructor(id){

        this.defaultMapEPSG = 'EPSG:3857';
        this.defaultAoiEPSG = "EPSG:4326";

        // register needed utm projections
        proj4.defs("EPSG:32629","+proj=utm +zone=29 +datum=WGS84 +units=m +no_defs");
        proj4.defs("EPSG:32630","+proj=utm +zone=30 +datum=WGS84 +units=m +no_defs")
        proj4.defs("EPSG:32631","+proj=utm +zone=31 +datum=WGS84 +units=m +no_defs");

        register(proj4);

        this.id = id;
        this.aois = [];
        
        this.selectedAoi = null;

        this.layers = this.initLayers();
        this.google.setVisible(false);
  

        this.initStyles();

        this.map = new Map({

            target: this.id,

            layers: this.layers,

            view: new View({
                center: [3598043, 149205],
                projection: this.defaultMapEPSG,
                zoom: 10,

            }),

        });

        // create click interaction for AOI selection with click
        this.select = new Select({

            // trigger only when there is a feature at the location of the click
            condition: (event) => {

                if (event.type === 'singleclick') {
                    return this.map.forEachFeatureAtPixel(event.pixel, function () {
                        return true;
                    });
                }

                return false;
            },
        });

        this.select.on('select', (e) => {

            let features = e.target.getFeatures();
            
            // if select triggered, select the first feature
            this.selectAoi(this.aois.find(aoi => aoi.id == features.item(0).get('id')));
        });

        this.map.addInteraction(this.select);
 
    }
    

    /**
     * Create the necessary layers
     * 
     * @returns {Array<Layer>} all the layers
     */
    initLayers() {

        this.osm = new TileLayer({
            source: new OSM(),
            properties : {
                'name' : 'OSM',
                'id' : 'osm'
            }
        });

        this.google = new TileLayer({
            source: new XYZ({
              url: "http://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
            }),
            properties : {
                'name' : 'Google',
                'id' : 'google'
            }
        }), 

        this.S2L1C = new TileLayer({

            source: new TileWMS({
              url: "https://services.sentinel-hub.com/ogc/wms/67c1d30d-c4dd-4fb5-ae53-b94df4e69d3c",
              params : {
                  urlProcessingApi: "https://services.sentinel-hub.com/ogc/wms/f9f27612-5c05-4d61-892e-78d50790da0e",
                  minZoom: 6,
                  maxZoom: 16,
                  preset: "1_TRUE_COLOR",
                  layers:"1_TRUE_COLOR"
                }
            }),
            properties : {
                'name' : 'S2-L1C',
                'id' : 'sentinel'
            }
        }),

        this.dropdownLayers = [
            this.osm,
            this.google,
            this.S2L1C,
        ]
    
        this.aoiLayer = new VectorLayer({
            source: new VectorSource({
                wrapX: false,
            }),
        });

        this.imageLayer = new ImageLayer({})

        return [
            this.osm,
            this.google,
            this.S2L1C,
            this.aoiLayer,
            this.imageLayer
        ];
    }

    /**
     * init styles
     */
    initStyles() {

        this.selectedStyle = new Style({
            stroke: new Stroke({ 
                color : "rgba(43, 113, 149, 0.6)",
                width : 4
            }),
        });

        this.defaultStyle = new Style({
            stroke: new Stroke({ 
                color : "rgba(43, 113, 149, 0.6)",
                width : 2
            }),
             fill: new Fill({
                color : "rgba(255, 255, 255, 0.4)"
            })
        });
    }

    /**
     * 
     * Creates features for AOIs and zooms to their extent
     * 
     * @param {Array<Object>} aois
     */
    setAois(aois)
    {
        this.aoiLayer.getSource().clear();
        this.features = [];
        this.aois = aois;

        aois.forEach((aoi) => {

            let feature = new Feature( {
                id : aoi.id,
                name : aoi.name
            })

            let geom = new Polygon(aoi.geom.coordinates);

            feature.setGeometry(geom.transform(
                this.defaultAoiEPSG,
                this.defaultMapEPSG
            ));

            this.features.push(feature);

        })

        this.aoiLayer.getSource().addFeatures(this.features);

        if(this.features.length > 0)
            this._flyTo(this.aoiLayer.getSource().getExtent(), 1);

    }

    /**
     * Sets the input aoi as selected AOI and sets its feature style to selected style
     * 
     * @param {Object} aoi 
     */
    selectAoi(aoi)
    {
        this.selectedAoi = aoi;

        // features are stored in seperate array as AOIs but are given the same ID as aoi.id
        let feature = this.features.find(f => f.get('id') == aoi.id);

        this._deselectFeatures();
        feature.setStyle(this.selectedStyle);
    }

    /**
     * Sets selected AOI to null and removes the selected feature style
     */
    deselectAois()
    {
        this.selectedAoi = null;
        this._deselectFeatures();
    }

    /**
     * Sets the image in amge layer to the image served from an url
     * 
     * @param {String} url url to image serving endpoint
     * @param {Array} extent extend of the image
     * @param {String} epsg the projeciton in whitch the extent is
     */
    setImage(url, extent, epsg)
    {
        // since the map is in web mercator projeciton, the extent has to be transformed to that projeciton
        let _extent = transformExtent(extent, epsg, this.defaultMapEPSG);

        let image = new ImageStatic({
            url: url,
            imageExtent : _extent,
        });

        this.imageLayer.setSource(image);
        this._flyTo(_extent, 1);

    }
    /**
     * Sets the opacity of the image layer
     * 
     * @param {Number} opacity float between 0 and 1
     */
    setImageOpacity(opacity)
    {
        this.imageLayer.setOpacity(opacity);
    }

    /**
     * Sets the background layer to given layer (must be one of the registered background layers)
     * 
     * @param {TileLayer} layer one of the background layers
     * @param {string} date1 - from date, used in sentinel layers
     * @param {string} date2 - to date, used in sentinel layers
     * 
     * @returns {TileLayer} active layer
     */
    setBackgroundLayer(layer, date1 = null, date2 = null)
    {
        // disable all layers
        this.dropdownLayers.forEach((x) => {
            x.setVisible(false);
        });

        // enable selected layer
        layer.setVisible(true);

        return layer;

    }

    /**
     * Sets sentinel layer time parameters
     * 
     * @param {string} date - to date, used in sentinel layers
     */
    setSentinelDate(date = null)
    {
        let sentinelSource = this.S2L1C.getSource();

        let timestring = '';
        
        if(date == null)
            timestring = '';

        else
            timestring = date;

        sentinelSource.updateParams({
            time: timestring,
        })
    }
    
    /**
     * Sets deselected style to all features
     */
    _deselectFeatures()
    {
        this.features.forEach(f => {
            f.setStyle(this.defaultStyle);
        })
    }

    /**
     * Makes a fly animation to zoom to the extent of choice on the map
     * 
     * @param {Array} extent the extent to zoom to
     * @param {Number} zoomOut how much to zoom out
     * @param {Number} duration duration of the animation in ms
     */
    _flyTo(extent, zoomOut = 2, duration = 500)
    {
        let view = this.map.getView();

        let resolution = view.getResolutionForExtent(extent);
        let zoom = view.getZoomForResolution(resolution) - zoomOut;
        let center = getCenter(extent);

        view.animate({
          center: center,
          duration: duration
        });

        view.animate({
          zoom: zoom,
          duration: duration
        });

    }



}
export default MainMap;
